import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Container,
  Autocomplete,
  MenuItem,
  Grid,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
// import FileUploadIcon from "@mui/icons-material/FileUpload";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers";
import useAxiosInstance from "../../hooks/useAxiosInstance";
import { API_RESULT, API_VERSION } from "../../enums/common.enum";
import { errorToast, handleError, successToast } from "../../utils/common";
import { format, addDays } from "date-fns";
import UploadFiles from "../../components/Common/uploadFiles";
import { DistributorForm } from "./AddDistributor";
import { Backdrop, CircularProgress } from "@mui/material";

const AddPurchase = () => {
  const navigate = useNavigate();
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState<Date | null>(new Date());
  const [dueDate, setDueDate] = useState<Date | null>(new Date());
  const [dueDateLabel, setDueDateLabel] = useState("");
  const [distributor, setDistributor] = useState("");
  const [invoiceAmount, setInvoiceAmount] = useState("0");
  const [payableAmount, setPayableAmount] = useState("0");
  // const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [distributors, setDistributors] = useState<any>([]);
  const [selectedDistributor, setSelectedDistributor] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [open, setOpen] = useState(false);

  const privateInstance = useAxiosInstance();

  const location = useLocation();
  const params: any = new URLSearchParams(location.search);
  const locationId = params.get("locationId");

  // const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   if (event.target.files && event.target.files[0]) {
  //     setSelectedFile(event.target.files[0]);
  //   }
  // };

  const addDistributorToList = (item: any) => {
    setDistributors((prev: any) => [...prev, ...[item]]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    let payload = {
      supplier_id: distributor,
      location_id: locationId,
      invoice_id: invoiceNumber,
      invoice_urls: uploadedFiles,
      invoice_date: format(new Date(String(invoiceDate)), "dd-MM-yyyy"),
      due_date: format(new Date(String(dueDate)), "dd-MM-yyyy"),
      invoice_amount: invoiceAmount,
      payable_amount: payableAmount,
    };
    setLoading(true);
    try {
      const result = await privateInstance.post(
        `${API_VERSION.V3}/suppliers/purchases`,
        payload
      );
      if (result?.data?.status === API_RESULT.SUCCESS) {
        successToast("Added successfully");
        // Redirect back to purchases page or dashboard
        navigate(`/distributors?locationId=${locationId}`);
      } else {
        errorToast(result.data.message);
      }
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const getDistributors = async () => {
      setLoading(true);
      try {
        const result = await privateInstance.get(
          `${API_VERSION.V3}/suppliers?location_id=${locationId}`
        );
        if (result?.data?.status === API_RESULT.SUCCESS) {
          setDistributors(result.data.response);
        } else {
          errorToast(result.data.message);
        }
      } catch (err) {
        handleError(err);
      } finally {
        setLoading(false);
      }
    };

    getDistributors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const distributorOptions = [
    ...distributors,
    { supplier_id: "add_new", name: "Add New Supplier", gst_no: "" },
  ];

  useEffect(() => {
    setDistributor(selectedDistributor);
    console.log(selectedDistributor);
    const selected = distributors.find((d: any) => d.supplier_id === selectedDistributor);
    if (selected?.credit_days) {
      const creditDays = parseInt(selected.credit_days || "0", 10);
      setDueDate(addDays(new Date(invoiceDate || new Date()), creditDays));
      setDueDateLabel("(Credit days - " + creditDays.toString() + ")");
    } else {
      setDueDate(addDays(new Date(invoiceDate || new Date()), 0));
      setDueDateLabel("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDistributor]);

  return (
    <>
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
          Add New Purchase
        </Typography>

        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Create a new purchase entry with invoice details
        </Typography>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Supplier
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Autocomplete
                size="small"
                fullWidth
                options={distributorOptions}
                filterOptions={(options, state) => {
                  const filtered = options.filter((option) =>
                    option.name.toLowerCase().includes(state.inputValue.toLowerCase())
                  );

                  const alreadyHasAddNew = filtered.some(
                    (option) => option.supplier_id === "add_new"
                  );

                  if (!alreadyHasAddNew) {
                    filtered.push({
                      supplier_id: "add_new",
                      name: "Add New Supplier",
                      gst_no: "",
                    });
                  }

                  return filtered;
                }}
                getOptionLabel={(option: any) =>
                  option.supplier_id === "add_new"
                    ? "Add New Supplier"
                    : `${option.name} - (GST NO.: ${option.gst_no})`
                }
                renderOption={(props, option) => (
                  <MenuItem
                    {...props}
                    key={option.supplier_id}
                    sx={
                      option.supplier_id === "add_new"
                        ? {
                            color: "#0058ff",
                            fontWeight: "bold",
                            textTransform: "uppercase",
                          }
                        : {}
                    }
                  >
                    {option.supplier_id === "add_new" ? (
                        "Add New Supplier"
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            width: "100%",
                            alignItems: "center",
                          }}
                        >
                          <Typography
                            variant="body1"
                            color="text.primary"
                            sx={{
                              maxWidth: "100%",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontSize: "0.6rem",
                            }}
                          >
                            {option.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: "#0058ff", fontSize: "0.6rem", marginLeft: 1, flexShrink: 0 }}
                          >
                            GST: {option.gst_no}
                          </Typography>
                        </Box>
                      )}
                  </MenuItem>
                )}
                value={
                  distributorOptions.find((d: any) => d.supplier_id === distributor) || null
                }
                onChange={(event, newValue) => {
                  if (!newValue) {
                    setDistributor("");
                    setSelectedDistributor("");
                    setDueDate(addDays(new Date(invoiceDate || new Date()), 0));
                    setDueDateLabel("");
                    return;
                  }

                  if (newValue.supplier_id === "add_new") {
                    setOpen(true);
                  } else {
                    setDistributor(newValue.supplier_id);
                    if (newValue?.credit_days) {
                      const creditDays = parseInt(newValue.credit_days || "0", 10);
                      setDueDate(addDays(new Date(invoiceDate || new Date()), creditDays));
                      setDueDateLabel("(Credit days - " + newValue?.credit_days.toString() + ")");
                    } else {
                      setDueDate(addDays(new Date(invoiceDate || new Date()), 0));
                      setDueDateLabel("");
                    }
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select distributor" />
                )}
              />
            </Box>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Invoice Number
            </Typography>
            <TextField
              size="small"
              fullWidth
              placeholder="Enter invoice number"
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
            />
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={6}>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Invoice Date
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label=""
                    inputFormat="dd-MM-yyyy"
                    mask={"__-__-____"}
                    value={invoiceDate}
                    onChange={(newValue) => setInvoiceDate(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        name="date"
                        autoComplete="off"
                        onKeyDown={(e: any) => {
                          e.preventDefault();
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: "space-between", alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2">
                    Due Date
                  </Typography>
                  {dueDateLabel && 
                    <Typography variant="caption"  sx={{ color: 'primary.main', fontSize: '0.7rem', ml: 1 }}>
                      {dueDateLabel}
                    </Typography>
                  }
                </Box>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label=""
                    inputFormat="dd-MM-yyyy"
                    mask={"__-__-____"}
                    value={dueDate}
                    onChange={(newValue) => setDueDate(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        name="date"
                        autoComplete="off"
                        onKeyDown={(e: any) => {
                          e.preventDefault();
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
              </Box>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={6}>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Invoice Amount (₹)
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  type="number"
                  value={invoiceAmount}
                  onChange={(e) => {
                    setInvoiceAmount(e.target.value);
                    setPayableAmount(e.target.value);
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Payable Amount (₹)
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  type="number"
                  value={payableAmount}
                  onChange={(e) => setPayableAmount(e.target.value)}
                />
              </Box>
              <Typography color="text.secondary" variant="body2">
                Amount to be paid after discounts (if any)
              </Typography>
            </Grid>
          </Grid>

          <UploadFiles
            title="Upload Invoice Images"
            uploadedFiles={uploadedFiles}
            setUploadedFiles={setUploadedFiles}
            orderId={invoiceNumber}
          />

          {/* <Box>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Upload Invoice Images
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              textAlign: "center",
              border: "1px dashed #ccc",
              borderRadius: 1,
              cursor: "pointer",
              bgcolor: "background.paper",
            }}
            onClick={() => document.getElementById("file-upload")?.click()}
          >
            <input
              id="file-upload"
              type="file"
              accept="image/png,image/jpeg,application/pdf"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <FileUploadIcon
              sx={{ fontSize: 40, color: "text.secondary", mb: 1 }}
            />
            <Typography>Click to upload or drag and drop</Typography>
            <Typography variant="body2" color="text.secondary">
              PNG, JPG or PDF (MAX. 5MB)
            </Typography>
            {selectedFile && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Selected: {selectedFile.name}
              </Typography>
            )}
          </Paper>
        </Box> */}

          <Button
            type="submit"
            color="primary"
            variant="contained"
            fullWidth
            size="large"
            sx={{
              mt: 2,
            }}
            disabled={
              !invoiceNumber?.trim() ||
              !distributor ||
              !invoiceAmount ||
              !payableAmount
            }
          >
            Add Purchase
          </Button>
        </Box>
      </Container>
      {open ? (
        <DistributorForm
          open={open}
          handleClose={() => setOpen(false)}
          addDistributorToList={addDistributorToList}
          setSelectedDistributor={setSelectedDistributor}
        />
      ) : null}

      <Backdrop
        open={loading}
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <CircularProgress sx={{ color: "#0058ff" }} />
      </Backdrop>
    </>
  );
};

export default AddPurchase;
